import { Close as CloseIcon, Search as SearchIcon } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import Button from "../../../components/ui/Button";
import { tokens } from "../../../theme";
import type { IBrandModel } from "../../../types/equipment";

interface SelectedGear {
  brandModel: IBrandModel;
  quantity: number;
}

interface GearSetInitialData {
  id: string;
  name: string;
  description: string;
  items?: {
    id: number;
    brand_model: number;
    brand_model_name: string;
    quantity: number;
    notes?: string;
  }[];
}

interface AddSetDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: {
    name: string;
    description: string;
    items: { brand_model: number; quantity: number; notes?: string }[];
  }) => void;
  brandModelList: IBrandModel[];
  preSelectedIds?: string[];
  initialData?: GearSetInitialData | null;
  isEdit?: boolean;
}

const BRAND = {
  textMuted: "#6B7280",
  badgeBg: "#FCE8E8",
  badgeText: "#EF4444",
};

const stockColor = (available: number, needed: number) => {
  if (available === 0)    return "#e05c5c";
  if (available < needed) return "#ffc107";
  return "#4caf7d";
};

const AddSetDialog: React.FC<AddSetDialogProps> = ({
  open,
  onClose,
  onSubmit,
  brandModelList,
  preSelectedIds = [],
  initialData = null,
  isEdit = false,
}) => {
  const theme  = useTheme();
  const colors = tokens(theme.palette.mode);

  const [name,        setName]        = useState("");
  const [description, setDescription] = useState("");
  const [search,      setSearch]      = useState("");

  const [selectedGear, setSelectedGear] = useState<Record<string, SelectedGear>>(() => {
    const init: Record<string, SelectedGear> = {};
    preSelectedIds.forEach((id) => {
      const bm = brandModelList.find((b) => b.id.toString() === id);
      if (bm) init[id] = { brandModel: bm, quantity: 1 };
    });
    return init;
  });

  React.useEffect(() => {
    if (!open) return;
    const init: Record<string, SelectedGear> = {};
    if (initialData?.items?.length) {
      initialData.items.forEach((item) => {
        if (!item.brand_model) return;
        const bmId = item.brand_model.toString();
        const bm   = brandModelList.find((b) => b.id.toString() === bmId);
        if (bm) init[bmId] = { brandModel: bm, quantity: item.quantity || 1 };
      });
    } else {
      preSelectedIds.forEach((id) => {
        const bm = brandModelList.find((b) => b.id.toString() === id);
        if (bm) init[id] = { brandModel: bm, quantity: 1 };
      });
    }
    setSelectedGear(init);
    setName(initialData?.name || "");
    setDescription(initialData?.description || "");
    setSearch("");
  }, [open, brandModelList, preSelectedIds, initialData]);

  const filteredBrandModels = useMemo(
    () =>
      brandModelList.filter((bm) => {
        const term = search.toLowerCase();
        return (
          (bm.brand_name || "").toLowerCase().includes(term) ||
          (bm.model      || "").toLowerCase().includes(term) ||
          (bm.category?.name || "").toLowerCase().includes(term)
        );
      }),
    [brandModelList, search],
  );

  const selectedList = Object.values(selectedGear);

  const toggleGear = (bm: IBrandModel) => {
    setSelectedGear((prev) => {
      const next = { ...prev };
      const id   = bm.id.toString();
      if (next[id]) { delete next[id]; }
      else          { next[id] = { brandModel: bm, quantity: 1 }; }
      return next;
    });
  };

  const updateQuantity = (id: string, delta: number) => {
    setSelectedGear((prev) => {
      const current = prev[id];
      if (!current) return prev;
      const newQty = Math.max(1, current.quantity + delta);
      return { ...prev, [id]: { ...current, quantity: newQty } };
    });
  };

  const removeGear = (id: string) => {
    setSelectedGear((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleSubmit = () => {
    if (!name.trim()) return;
    onSubmit({
      name,
      description,
      items: selectedList.map((g) => ({
        brand_model: Number(g.brandModel.id),
        quantity:    g.quantity,
        notes:       "",
      })),
    });
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "50px",
      backgroundColor: colors.primary[800],
      "& fieldset": { border: "none" },
    },
    "& input": { fontSize: 12, py: 1.4 },
  };

  const customScrollbar = {
    "&::-webkit-scrollbar": { width: "6px" },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": { background: colors.primary[400], borderRadius: "10px" },
  };


  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "24px",
          p: 3,
          backgroundColor: theme.palette.background.paper,
          boxShadow: "0px 10px 40px rgba(0, 0, 0, 0.08)",
        },
      }}
    >
      <DialogContent sx={{ p: 0, overflow: "hidden" }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h5" fontWeight={700} color={colors.primary[400]}>
              {isEdit ? "Edit Gear Set" : "Create Gear Set"}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ border: `1.5px solid ${colors.primary[100]}`, borderRadius: "50%", color: colors.primary[100], p: 0.5 }}
          >
            <CloseIcon fontSize="small" sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <Box display="flex" flexDirection="column" gap={2} sx={{ height: "65vh", minHeight: 540 }}>
          <Box display="flex" gap={4} flex={1} sx={{ overflow: "hidden" }}>

            {/* Left panel */}
            <Box flex={1.2} display="flex" flexDirection="column" gap={2.5}>
              <Box>
                <Typography variant="body2" fontWeight={600} mb={0.5} color={BRAND.textMuted}>
                  Gear Set Name<span style={{ color: "#EF4444" }}>*</span>
                </Typography>
                <TextField fullWidth placeholder="e.g. Muzukuru Lux" value={name} onChange={(e) => setName(e.target.value)} sx={inputSx} />
              </Box>

              <Box>
                <Typography variant="body2" fontWeight={600} mb={0.5} color={BRAND.textMuted}>
                  Description (Optional)
                </Typography>
                <TextField fullWidth placeholder="e.g. description" value={description} onChange={(e) => setDescription(e.target.value)} sx={inputSx} />
              </Box>

              <Box>
                <Typography variant="body2" fontWeight={600} mb={1} color={BRAND.textMuted}>
                  Search Gear From Inventory
                </Typography>
                <TextField
                  fullWidth
                  placeholder="search gear here"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: BRAND.textMuted }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={inputSx}
                />
              </Box>

              {/* Brand Model List */}
              <Box
                sx={{
                  flex: 1,
                  backgroundColor: colors.primary[800],
                  borderRadius: "16px",
                  overflowY: "auto",
                  pr: 1,
                  py: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  ...customScrollbar,
                }}
              >
                {filteredBrandModels.map((bm, index) => {
                  const bmId      = bm.id.toString();
                  const isChecked = !!selectedGear[bmId];
                  const qty       = selectedGear[bmId]?.quantity ?? 1;
                  const available = bm.available_quantity ?? 0;
                  const total     = bm.brand_model_quantity  ?? 0;
                  const color     = stockColor(available, isChecked ? qty : 1);
                  const shortage  = isChecked && available < qty;

                  return (
                    <React.Fragment key={bmId}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.2,
                          px: 1.5,
                          py: 1,
                          borderRadius: "12px",
                          transition: "background-color 0.2s",
                          backgroundColor: shortage ? "rgba(224,92,92,0.06)" : "transparent",
                        }}
                      >
                        <Checkbox
                          checked={isChecked}
                          onChange={() => toggleGear(bm)}
                          size="medium"
                          sx={{ color: BRAND.textMuted, "&.Mui-checked": { color: colors.primary[400] }, p: 0 }}
                        />
                        <Box flex={1}>
                          <Typography variant="body1" fontWeight={600} color={colors.primary[100]}>
                            {bm.brand_name} {bm.model}
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                            <Chip
                              label={bm.category?.name || "Uncategorized"}
                              size="small"
                              sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700, borderRadius: "6px" }}
                            />
                            {/* Stock badge */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                              <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: color }} />
                              <Typography sx={{ fontSize: 11, color, fontWeight: 500 }}>
                                {available}/{total} available
                                {shortage && " ⚠️"}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        {isChecked && (
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <IconButton size="small" onClick={() => updateQuantity(bmId, -1)} sx={{ color: colors.primary[100], p: 0 }}>
                              <Typography fontSize={20} fontWeight={500}>−</Typography>
                            </IconButton>
                            <Box sx={{
                              minWidth: 44, height: 24,
                              border: `1px solid ${shortage ? "#e05c5c" : colors.primary[800]}`,
                              borderRadius: "16px",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              backgroundColor: colors.primary[900],
                            }}>
                              <Typography variant="body2" fontWeight={600} color={shortage ? "#e05c5c" : colors.primary[100]}>
                                {qty}
                              </Typography>
                            </Box>
                            <IconButton size="small" onClick={() => updateQuantity(bmId, 1)} sx={{ color: colors.primary[100], p: 0 }}>
                              <Typography fontSize={20} fontWeight={500}>+</Typography>
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                      {index < filteredBrandModels.length - 1 && (
                        <Divider sx={{ borderColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)", ml: 1.5 }} />
                      )}
                    </React.Fragment>
                  );
                })}
              </Box>
            </Box>

            {/* Right panel — selected */}
            <Box flex={0.8} display="flex" flexDirection="column" gap={2}>
              <Box display="flex" alignItems="center" gap={1.5}>
                <Typography variant="subtitle1" fontWeight={600} color={colors.primary[100]}>
                  Selected
                </Typography>
                <Chip
                  label={selectedList.length}
                  size="small"
                  sx={{ height: 22, fontSize: "0.75rem", backgroundColor: BRAND.badgeBg, color: BRAND.badgeText, fontWeight: 700, borderRadius: "12px", minWidth: 28 }}
                />
              </Box>

              <Box
                sx={{
                  flex: 1, overflowY: "auto",
                  display: "flex", flexDirection: "column", gap: 1,
                  pr: 1, ...customScrollbar,
                  backgroundColor: colors.primary[800],
                  borderRadius: "16px", p: 1.2,
                }}
              >
                {selectedList.map(({ brandModel, quantity }) => {
                  const available = brandModel?.available_quantity ?? 0;
                  const shortage  = available < quantity;
                  return (
                    <Box
                      key={brandModel.id}
                      sx={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        px: 1.5, py: 1, borderRadius: "12px",
                        border: `1px solid ${shortage ? "#e05c5c44" : colors.primary[800]}`,
                        backgroundColor: colors.primary[900],
                      }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight={600} color={colors.primary[100]}>
                          {brandModel.brand_name} {brandModel.model}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={0.5} mt={0.25}>
                          <Typography variant="caption" color={BRAND.textMuted} fontWeight={500}>
                            ×{quantity}
                          </Typography>
                          {shortage && (
                            <Typography variant="caption" sx={{ color: "#ffc107", display: "flex", alignItems: "center", gap: 0.3 }}>
                              · only {available} available
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <IconButton size="small" onClick={() => removeGear(brandModel.id.toString())}>
                        <CloseIcon sx={{ fontSize: 18, color: BRAND.textMuted }} />
                      </IconButton>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>

          <Box display="flex" gap={2} justifyContent="flex-end">
            <Button variant="outlined" onClick={onClose} sx={{ borderRadius: "12px", height: 48, fontWeight: 600, px: 3 }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!name.trim()}
              sx={{ borderRadius: "12px", height: 48, fontWeight: 600, px: 3 }}
            >
              {isEdit ? "Update Gear Set" : "Save Gear Set"}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AddSetDialog;